// Phase 5 (web revival) — Stream A.
//
// Three stacked cards inside the authed AppLayout:
//   1. Profile      — read-only echo of /v1/users/me
//   2. Change pw    — POST /v1/auth/password/change, re-mints the token
//                     (server bumps token_version) so we feed the fresh
//                     AuthEnvelope back into setSession to stay signed in.
//   3. Sign out everywhere — POST /v1/auth/logout-all, which bumps
//                     token_version server-side (closes every WS) and
//                     revokes every refresh token. We then clearSession()
//                     and route to /login.
//
// All three sections own their own mutation/query state so a failure in
// one card never blanks the others.

import { useState, useMemo, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api, ApiError } from '../api/client';
import { qk } from '../app/queryClient';
import { setSession, clearSession } from '../app/auth';
import { disconnect as disconnectWS } from '../app/wsClient';
import type {
  AuthEnvelope,
  PasswordChangeRequest,
  UserMeDTO,
} from '../api/types';

const MIN_PASSWORD_LENGTH = 12;

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;
  const TB = GB * 1024;
  if (bytes >= TB) return `${(bytes / TB).toFixed(2)} TB`;
  if (bytes >= GB) return `${(bytes / GB).toFixed(2)} GB`;
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
  if (bytes >= KB) return `${(bytes / KB).toFixed(1)} KB`;
  return `${bytes} B`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export function SettingsPage() {
  return (
    <div className="h-full overflow-y-auto bg-neutral-950 px-4 py-8">
      <div className="mx-auto w-full max-w-2xl space-y-6">
        <h1 className="font-display text-2xl font-medium tracking-tight text-neutral-100">
          Settings
        </h1>
        <ProfileCard />
        <ChangePasswordCard />
        <SignOutEverywhereCard />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// (a) Profile
// ──────────────────────────────────────────────────────────────────────

function ProfileCard() {
  const me = useQuery({
    queryKey: qk.me(),
    queryFn: () => api.get<UserMeDTO>('/v1/users/me'),
  });

  return (
    <section className="rounded-xl border border-white/5 bg-neutral-900/60 p-6">
      <h2 className="mb-4 text-base font-semibold text-neutral-100">Profile</h2>
      {me.isLoading && (
        <p className="text-sm text-neutral-500">Loading…</p>
      )}
      {me.isError && (
        <p className="text-sm text-red-300">
          {me.error instanceof ApiError
            ? me.error.reason
            : 'Failed to load profile.'}
        </p>
      )}
      {me.data && (
        <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
          <ProfileRow label="Display name" value={me.data.display_name ?? '—'} />
          <ProfileRow label="Email" value={me.data.email ?? '—'} />
          <ProfileRow label="Member since" value={formatDate(me.data.created_at)} />
          <ProfileRow
            label="Storage used"
            value={formatBytes(me.data.storage_bytes_used)}
          />
          <ProfileRow
            label="Assets"
            value={me.data.asset_count.toLocaleString()}
          />
        </dl>
      )}
    </section>
  );
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-neutral-500">{label}</dt>
      <dd className="text-neutral-100">{value}</dd>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// (b) Change password
// ──────────────────────────────────────────────────────────────────────

function ChangePasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const mutation = useMutation<AuthEnvelope, ApiError, PasswordChangeRequest>({
    mutationFn: (body) =>
      api.post<AuthEnvelope>('/v1/auth/password/change', body),
    onSuccess: (env) => {
      // Server bumped token_version and re-minted; keep the user signed
      // in by adopting the fresh envelope.
      setSession(env);
      setCurrent('');
      setNext('');
      setConfirm('');
      setShowSuccess(true);
    },
  });

  const mismatch = next.length > 0 && confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const inlineError = useMemo(() => {
    if (tooShort) return `New password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    if (mismatch) return 'New password and confirmation do not match.';
    return null;
  }, [tooShort, mismatch]);

  const serverErr = mutation.error;
  let serverErrorMessage: string | null = null;
  if (serverErr) {
    if (serverErr.code === 'auth.invalid_credentials') {
      serverErrorMessage = 'Current password is incorrect.';
    } else {
      serverErrorMessage = serverErr.reason;
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setShowSuccess(false);
    if (!current || !next || !confirm) return;
    if (next !== confirm) return;
    if (next.length < MIN_PASSWORD_LENGTH) return;
    mutation.mutate({ current_password: current, new_password: next });
  }

  const submitting = mutation.isPending;
  const canSubmit =
    !submitting &&
    current.length > 0 &&
    next.length >= MIN_PASSWORD_LENGTH &&
    next === confirm;

  const inputClass =
    'w-full rounded-md border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm text-neutral-100 placeholder:text-neutral-500 focus-visible:border-neutral-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-600 disabled:opacity-50';

  return (
    <section className="rounded-xl border border-white/5 bg-neutral-900/60 p-6">
      <h2 className="mb-4 text-base font-semibold text-neutral-100">
        Change password
      </h2>
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <PasswordField
          id="settings-current"
          label="Current password"
          autoComplete="current-password"
          value={current}
          onChange={setCurrent}
          disabled={submitting}
          inputClass={inputClass}
        />
        <PasswordField
          id="settings-new"
          label={`New password (min ${MIN_PASSWORD_LENGTH} characters)`}
          autoComplete="new-password"
          value={next}
          onChange={(v) => {
            setNext(v);
            setShowSuccess(false);
          }}
          disabled={submitting}
          inputClass={inputClass}
        />
        <PasswordField
          id="settings-confirm"
          label="Confirm new password"
          autoComplete="new-password"
          value={confirm}
          onChange={(v) => {
            setConfirm(v);
            setShowSuccess(false);
          }}
          disabled={submitting}
          inputClass={inputClass}
        />
        {inlineError && (
          <p className="text-xs text-amber-300">{inlineError}</p>
        )}
        {serverErrorMessage && (
          <p
            role="alert"
            className="rounded-md border border-red-900/60 bg-red-950/40 px-3 py-2 text-xs text-red-300"
          >
            {serverErrorMessage}
          </p>
        )}
        {showSuccess && (
          <p className="rounded-md border border-emerald-900/60 bg-emerald-950/40 px-3 py-2 text-xs text-emerald-300">
            Password updated.
          </p>
        )}
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-900 transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </section>
  );
}

function PasswordField({
  id,
  label,
  autoComplete,
  value,
  onChange,
  disabled,
  inputClass,
}: {
  id: string;
  label: string;
  autoComplete: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  inputClass: string;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-xs font-medium text-neutral-300">
        {label}
      </label>
      <input
        id={id}
        type="password"
        autoComplete={autoComplete}
        required
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// (c) Sign out everywhere
// ──────────────────────────────────────────────────────────────────────

function SignOutEverywhereCard() {
  const navigate = useNavigate();

  const mutation = useMutation<void, ApiError, void>({
    mutationFn: () => api.post<void>('/v1/auth/logout-all', {}),
    onSuccess: () => {
      // Server bumped token_version → every WS is being torn down by
      // EventBus.closeAllForUser. Tear down our own WS + drop local
      // session, then bounce to login.
      disconnectWS();
      clearSession();
      navigate({ to: '/login' });
    },
  });

  function handleClick() {
    const confirmed = window.confirm(
      'Sign out of every device, including this one?',
    );
    if (!confirmed) return;
    mutation.mutate();
  }

  const err = mutation.error;

  return (
    <section className="rounded-xl border border-white/5 bg-neutral-900/60 p-6">
      <h2 className="mb-2 text-base font-semibold text-neutral-100">
        Sign out everywhere
      </h2>
      <p className="mb-4 text-sm text-neutral-400">
        Revoke every active session on every device. You will need to sign in
        again on each one.
      </p>
      {err && (
        <p
          role="alert"
          className="mb-3 rounded-md border border-red-900/60 bg-red-950/40 px-3 py-2 text-xs text-red-300"
        >
          {err.reason}
        </p>
      )}
      <button
        type="button"
        onClick={handleClick}
        disabled={mutation.isPending}
        className="rounded-md border border-red-900/70 bg-red-950/40 px-4 py-2 text-sm font-medium text-red-200 transition-colors hover:bg-red-900/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {mutation.isPending ? 'Signing out…' : 'Sign out everywhere'}
      </button>
    </section>
  );
}
